import {
  containerCargoUsedM3,
  freeCargoM3,
  freeOreHoldM3,
  generalCargoUsedM3,
  oreUsedM3,
  taggedCargoUsedM3
} from "../../acceptedMissions";
import { getSystem, loadSystems } from "../../data/loadSystems";
import { bestSellStation, currentMarketEpoch, type GalaxyRegion } from "../../market/stationMarket";
import { systemsWithinHops } from "../../galaxyGraph";
import { formatEndurance, fuelHeadline, fuelSummary, provisionsSummary } from "../../endurance";
import type { PlayerShip } from "../../types";
import { RESOURCE_TIER, volumePerUnitM3 } from "../../resourceCatalog";
import { MARKET_ITEMS, marketPricePerUnit } from "../../data/marketItems";
import { BASE_STORAGE_CAPS, categoryFreeM3, categoryUsedM3, findBaseAt, transferFromBase, transferToBase, type BaseRecord } from "../../baseStorage";
import { triggerSave } from "../persistence";
import { state } from "../state";
import type { ScreenContext } from "../app";
import { renderHeader } from "../components";

// Cargo UI (design doc Section 11a, LOCKED — Phase 2 wireframe). The doc
// specifies one shared overlay used in three contexts (in space, docked at
// your own Base, docked at a faction Station), each unlocking different
// actions, plus a right-hand panel for Base storage or another fleet
// ship's cargo. Two of the three are built:
// - **Station context** -- a real per-System `hasStation` flag (Section
//   6) gates Sell; per the doc's own rule, no Transfer/Base-storage panel
//   here at all (a faction Station never has a Base).
// - **Docked at your own Base (16.36)** -- a right-hand panel shows that
//   Base's real storage (src/baseStorage.ts, segmented into named
//   modules with their own m3 caps) with a bidirectional Transfer action.
//   Gated on `findOwnedBase()`, which matches state.bases against the
//   ship's *current* system+Location -- true whether this screen was
//   reached from Base View's Cargo Warehouse/Resource Stockpile lot
//   modules or from the Expedition View's own Cargo nav icon while
//   sitting at a leased planet.
// - **In space (attended mode)** -- now real too. This screen opens as a
//   semi-transparent pane over the live Expedition View (16.38), which is
//   exactly the doc's own "target bar and module rack stay visible" rule,
//   so Jettison finally has the context it needs. Offered only when the
//   underlying View is the Expedition View, never at a dock.
//
// Stack splitting (Section 15's open "partial jettison/sell/transfer of a
// single cargo item stack") is real as well: every row carries a quantity
// field defaulting to the whole stack, and Sell/Store/Retrieve/Jettison
// all act on that quantity, in UNITS (16.40) -- see CargoRow's own comment
// for why "qty" and "m3" are no longer the same number for real ore.
// Also not modeled: **Scrap** is a listed action for non-marketable items,
// but every real resource and every Market catalog good has a real price
// (nothing in the data model produces an unmarketable item), so it's
// omitted rather than added as a button with nothing to ever act on.
// Likewise **press-and-hold item detail/zoom** is skipped -- there's no
// additional per-item image/description data beyond what's already shown
// inline. **Value** uses marketPricePerUnit() (src/data/marketItems.ts)
// -- the 9 real mining resources keep their real tiered CR/m3 (16.2), and
// anything else (a Market-bought good, or wreck loot -- see
// expeditionScreen.ts) uses the Market catalog's own price, so cargo
// containing either kind never crashes or shows a blank price.

type SortMode = "value" | "volume" | "type";

// Cargo volume warning (16.57): the hold's own bar shifts color as it
// fills -- amber past 80% full, red past 95% -- rather than a separate
// banner/toast.
function volumeBarHtml(usedM3: number, capacityM3: number): string {
  const pct = capacityM3 > 0 ? Math.min(100, (usedM3 / capacityM3) * 100) : 0;
  const level = pct >= 95 ? "critical" : pct >= 80 ? "warning" : "normal";
  return `
    <div class="cargo-volume-bar" data-level="${level}">
      <div class="cargo-volume-bar-fill" style="width:${pct}%"></div>
    </div>
  `;
}

/**
 * Fuel and Provisions, with the rates that drain them (16.103, Don).
 *
 * Don's ask: "in the cargo view where we can see how much fuel/provisions we
 * have we should also view our consumption rates to gauge when to go refill."
 *
 * This screen already showed both pools. What it could not answer is the only
 * question anyone asks of a pool -- how long it lasts -- because the rate lived
 * in `fuel.ts` and `provisions.ts` and appeared nowhere.
 *
 * Note that neither is cargo. Fuel and Provisions occupy no hold space; they are
 * separate ship pools, and they sit above the manifest here rather than inside it
 * so that distinction stays visible.
 */
function enduranceHtml(ship: PlayerShip): string {
  const fuel = fuelSummary(ship.hullTierLabel, state.fuelOnBoard, state.fuelCapacity);
  const prov = provisionsSummary(ship.crew, state.provisionsOnBoard, state.provisionsCapacity);
  return `
    <div class="lot-stat-grid cargo-endurance">
      <div${fuel.stranded ? ' class="lot-stat-low"' : ""}>
        <span>Fuel</span>
        <span>${Math.round(fuel.onBoard)} / ${Math.round(fuel.capacity)} &mdash; ${fuelHeadline(fuel)}</span>
      </div>
      <div>
        <span>Fuel spent per</span>
        <span>${fuel.fastTravelCost} a jump, ${fuel.warpCost} a warp, nothing to Move To</span>
      </div>
      <div${prov.low ? ' class="lot-stat-low"' : ""}>
        <span>Provisions</span>
        <span>${prov.onBoard.toFixed(1)} / ${prov.capacity.toFixed(1)} &mdash; ${formatEndurance(prov.hoursLeft)} left</span>
      </div>
      <div>
        <span>Provisions eaten per hour</span>
        <span>${prov.perHour.toFixed(1)} by ${prov.crew} crew</span>
      </div>
    </div>
    <p class="dim cargo-endurance-note">
      Neither counts against the hold. Both restock at any Station Market, where Fuel and
      Provisions are always in stock.
    </p>
  `;
}

function findOwnedBase(): BaseRecord | undefined {
  return findBaseAt(state.bases, state.currentSystemId, state.currentLocationId);
}

/**
 * Where this item sells best, and how far that is (16.98, Don's direction).
 *
 * The hold is the one screen a captain reads while deciding what to do next out
 * in space, and until now it priced everything at the catalogue average and said
 * nothing about where to take it. The Market screen knew; the Cargo screen, which
 * is the one available in flight, did not.
 *
 * Absolute best sell price rather than the Market's buy-here-sell-there margin:
 * these goods are already owned, so there is no purchase to net off.
 */
interface BestMarket {
  systemId: string;
  systemName: string;
  pricePerUnit: number;
  hops: number | null;
}

function bestMarketFor(
  resource: string,
  stations: Array<{ id: string; name: string; region: GalaxyRegion }>,
  hopsById: Map<string, number>,
  epoch: number
): BestMarket | null {
  const best = bestSellStation(resource, stations, epoch);
  if (!best) return null;
  const station = stations.find((s) => s.id === best.stationId);
  if (!station) return null;
  return {
    systemId: station.id,
    systemName: station.name,
    pricePerUnit: best.pricePerUnit,
    hops: hopsById.get(station.id) ?? null
  };
}

function rowFor(resource: string, qty: number, best: BestMarket | null = null): CargoRow {
  const pricePerUnit = marketPricePerUnit(resource);
  const vpu = volumePerUnitM3(resource);
  return {
    resource,
    qty,
    m3: qty * vpu,
    vpu,
    group: groupLabel(resource),
    pricePerUnit,
    totalCr: qty * pricePerUnit,
    best
  };
}

function baseRows(base: BaseRecord): CargoRow[] {
  // Base storage is not going anywhere, so it needs no route advice.
  return Object.entries(base.storage)
    .filter(([, qty]) => qty > 0)
    .map(([resource, qty]) => rowFor(resource, qty));
}

// `qty` is the raw stored amount (16.40: UNITS for real ore, still
// numerically == m3 for anything else). `m3` is the derived volume that
// quantity actually occupies -- the two diverge only for the 9 real
// resources, whose per-tier volumePerUnitM3 isn't 1.
interface CargoRow {
  resource: string;
  qty: number;
  m3: number;
  vpu: number; // volumePerUnitM3 -- kept on the row so the UI can skip a redundant "(X m3)" when it's 1
  group: string; // "Tier N" for a real mining resource, else its Market category
  pricePerUnit: number; // real CR per stored unit (marketPricePerUnit already bakes in volume for ore)
  totalCr: number;
  /** Best market in the galaxy for this item, null for anything unsellable (16.98). */
  best: BestMarket | null;
}

function groupLabel(resource: string): string {
  const tier = RESOURCE_TIER[resource];
  if (tier !== undefined) return `Tier ${tier}`;
  return MARKET_ITEMS.find((i) => i.name === resource)?.category ?? "Unknown";
}

function buildRows(bestFor: (resource: string) => BestMarket | null): CargoRow[] {
  return Object.entries(state.cargo)
    .filter(([, qty]) => qty > 0)
    .map(([resource, qty]) => rowFor(resource, qty, bestFor(resource)));
}

function sortRows(rows: CargoRow[], mode: SortMode): CargoRow[] {
  const sorted = [...rows];
  if (mode === "value") {
    sorted.sort((a, b) => b.totalCr - a.totalCr);
  } else if (mode === "volume") {
    sorted.sort((a, b) => b.m3 - a.m3);
  } else {
    sorted.sort((a, b) => a.group.localeCompare(b.group) || a.resource.localeCompare(b.resource));
  }
  return sorted;
}

export function renderCargoScreen(container: HTMLElement, ctx: ScreenContext): void {
  if (!state.currentShip) {
    ctx.navigate("ships");
    return;
  }
  // A Loaner Ship's cargo cannot be managed at all (16.63) -- no Buy/
  // Sell/Transfer/Jettison, real restriction, not just a hidden button.
  if (state.isLoanerShip) {
    renderHeader(container, ctx);
    const notice = document.createElement("div");
    notice.innerHTML = `
      <h1>Cargo</h1>
      <p class="dim">This is a Loaner Ship (16.63) -- its cargo cannot be managed. Complete Faction Missions with it, or reclaim/buy a real ship to manage cargo again.</p>
    `;
    container.appendChild(notice);
    return;
  }
  const ship = state.currentShip;

  let sortMode: SortMode = "value";
  const selected = new Set<string>(); // ship-cargo selection (Sell/Store/Jettison)
  const selectedBase = new Set<string>(); // Base-storage selection (Retrieve)
  // Per-item partial quantities (stack splitting). Absent = "the whole
  // stack", so nothing needs pre-populating and a stack that grows while
  // this screen is open still defaults to all of it.
  const qtyByItem = new Map<string, number>();
  const qtyByBaseItem = new Map<string, number>();

  // How much of `name` the pending action should move: the typed partial if
  // there is one, otherwise everything, always clamped to what's actually
  // there (a stack can shrink under the input -- mining/looting keep
  // running in the Expedition View underneath this pane).
  function pendingQty(name: string, available: number, from: Map<string, number>): number {
    const raw = from.get(name);
    return Math.max(0, Math.min(raw ?? available, available));
  }

  renderHeader(container, ctx);

  const wrap = document.createElement("div");
  container.appendChild(wrap);

  function render(): void {
    // Recomputed every render, not cached at mount -- state.currentSystemId
    // can change while this screen is up (e.g. a waypoint "Travel" jump via
    // the header), and Sell's/Transfer's availability must track that live.
    const system = getSystem(state.currentSystemId);
    const hasStation = system?.hasStation ?? false;
    const base = findOwnedBase();
    // In space (Section 11a's "attended mode" context) rather than docked:
    // Cargo UI is a pane over the live Expedition View (16.38), which is
    // exactly what makes Jettison possible here at last -- the doc's own
    // rule is that jettisoning happens in space, never at a dock.
    const inSpace = state.lastTopLevelView === "expedition";
    // Best-market context (16.98). Recomputed per render alongside `system`
    // above, for the same reason: the ship can jump while this pane is open, and
    // a stale hop count is worse than none.
    const marketEpoch = currentMarketEpoch(Date.now());
    const allSystems = loadSystems();
    const stations = allSystems
      .filter((s) => s.hasStation)
      .map((s) => ({ id: s.id, name: s.name, region: s.region as GalaxyRegion }));
    const hopsById = new Map<string, number>(
      systemsWithinHops(state.currentSystemId, Infinity, allSystems).map((d) => [d.system.id, d.hops])
    );
    const rows = sortRows(
      buildRows((resource) => bestMarketFor(resource, stations, hopsById, marketEpoch)),
      sortMode
    );
    // Two independent allowances now (16.39): general cargo for loot/goods/
    // sealed containers, ore hold for raw mined resources.
    const used = generalCargoUsedM3(state.cargo);
    const containers = containerCargoUsedM3(state.acceptedMissions);
    const free = freeCargoM3(ship.cargoM3, state.cargo, state.acceptedMissions);
    const oreUsed = oreUsedM3(state.cargo);
    const tagged = taggedCargoUsedM3(state.acceptedMissions);
    const oreFree = freeOreHoldM3(ship.oreHoldM3, state.cargo, state.acceptedMissions);
    const selectedRows = rows.filter((r) => selected.has(r.resource));
    const selectedTotalCr = selectedRows.reduce(
      (sum, r) => sum + r.pricePerUnit * pendingQty(r.resource, r.qty, qtyByItem),
      0
    );

    wrap.innerHTML = `
      <h1>Cargo</h1>
      <p class="dim">
        ${ship.name} &mdash;
        <strong>Cargo</strong> ${used.toFixed(1)} / ${ship.cargoM3} m3${
          containers > 0 ? ` (incl. ${containers.toFixed(0)} m3 of sealed containers)` : ""
        }, ${free.toFixed(1)} free
        &nbsp;·&nbsp;
        <strong>Ore Hold</strong> ${oreUsed.toFixed(1)} / ${ship.oreHoldM3} m3${
          tagged > 0 ? ` (+ ${tagged.toFixed(1)} m3 reserved for mining quotas)` : ""
        }, ${oreFree.toFixed(1)} free
      </p>
      ${volumeBarHtml(used + containers, ship.cargoM3)}
      ${volumeBarHtml(oreUsed + tagged, ship.oreHoldM3)}
      ${enduranceHtml(ship)}
      ${
        hasStation
          ? `<p class="dim">Docked at ${system!.name} Station -- Sell available.</p>`
          : base
            ? `<p class="dim">Docked at your Base -- Transfer with Base storage available.</p>`
            : `<p class="dim">${system ? system.name : state.currentSystemId} has no Station -- Sell requires docking at a Station.</p>`
      }

      <div class="cargo-columns">
        <div class="cargo-column">
          <div class="filter-row">
            <span class="dim">Sort:</span>
            ${(["value", "volume", "type"] as SortMode[])
              .map((m) => `<button type="button" class="pill-btn ${sortMode === m ? "active" : ""}" data-sort="${m}">${sortLabel(m)}</button>`)
              .join("")}
          </div>

          <!-- 16.96's lesson, applied here before it bites: two prices sit on
               every row and they are not the same price. Saying which is which
               costs one line and prevents the exact confusion the Market screen
               caused by leaving it implied. -->
          ${
            rows.length > 0
              ? `<p class="dim cargo-price-key">
                   <strong>CR/unit</strong> is the catalogue value &mdash; the right-hand button is what the
                   best station in the galaxy actually pays, and how far it is. Press it to set course.
                 </p>`
              : ""
          }

          <div class="cargo-list">
            ${
              rows.length === 0
                ? '<p class="dim">Cargo hold is empty.</p>'
                : rows
                    .map((r) =>
                      cargoRowHtml(
                        r,
                        selected.has(r.resource),
                        "data-select",
                        pendingQty(r.resource, r.qty, qtyByItem),
                        "data-qty"
                      )
                    )
                    .join("")
            }
          </div>

          <div class="cargo-actions">
            <span class="dim">${selectedRows.length > 0 ? `${selectedRows.length} selected -- ${Math.round(selectedTotalCr).toLocaleString()} CR` : "Select cargo"}</span>
            <button type="button" data-sell ${hasStation && selectedRows.length > 0 ? "" : "disabled"}>Sell Selected</button>
            ${base ? `<button type="button" data-store ${selectedRows.length > 0 ? "" : "disabled"}>Store Selected →</button>` : ""}
            ${inSpace ? `<button type="button" class="btn-danger" data-jettison ${selectedRows.length > 0 ? "" : "disabled"}>Jettison</button>` : ""}
          </div>
        </div>

        ${base ? baseStoragePanelHtml(base, selectedBase, (n, a) => pendingQty(n, a, qtyByBaseItem)) : ""}
      </div>
    `;

    wrap.querySelectorAll<HTMLButtonElement>("button[data-sort]").forEach((btn) => {
      btn.addEventListener("click", () => {
        sortMode = btn.dataset.sort as SortMode;
        render();
      });
    });

    // Best-market waypoint (16.98). Identical toggle semantics to the Market
    // screen and Galaxy Prices, so there is one behaviour to learn rather than
    // three. Saved, because a course is real state and not a UI preference.
    wrap.querySelectorAll<HTMLButtonElement>("[data-cargo-waypoint]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.cargoWaypoint!;
        const name = getSystem(id)?.name ?? id;
        if (state.waypointSystemId === id) {
          state.waypointSystemId = null;
          state.lastEvent = `Course to ${name} cleared.`;
        } else {
          state.waypointSystemId = id;
          state.lastEvent = `Course set for ${name}.`;
        }
        triggerSave(ctx);
        ctx.rerender();
      });
    });

    wrap.querySelectorAll<HTMLInputElement>("input[data-select]").forEach((el) => {
      el.addEventListener("change", () => {
        const resource = el.dataset.select!;
        if (el.checked) {
          selected.add(resource);
        } else {
          selected.delete(resource);
        }
        render();
      });
    });

    // Partial-quantity inputs (stack splitting). "input" rather than
    // "change" so the running CR total tracks typing, but the value is
    // only stored -- no re-render per keystroke, which would steal focus
    // out of the field being typed into.
    wrap.querySelectorAll<HTMLInputElement>("input[data-qty]").forEach((el) => {
      el.addEventListener("input", () => {
        const parsed = Number.parseFloat(el.value);
        if (Number.isFinite(parsed)) qtyByItem.set(el.dataset.qty!, parsed);
      });
    });
    wrap.querySelectorAll<HTMLInputElement>("input[data-qty-base]").forEach((el) => {
      el.addEventListener("input", () => {
        const parsed = Number.parseFloat(el.value);
        if (Number.isFinite(parsed)) qtyByBaseItem.set(el.dataset.qtyBase!, parsed);
      });
    });

    wrap.querySelector<HTMLButtonElement>("button[data-sell]")!.addEventListener("click", () => {
      sellSelected();
    });

    wrap.querySelector<HTMLButtonElement>("button[data-jettison]")?.addEventListener("click", () => {
      jettisonSelected();
    });

    if (base) {
      wrap.querySelectorAll<HTMLInputElement>("input[data-select-base]").forEach((el) => {
        el.addEventListener("change", () => {
          const resource = el.dataset.selectBase!;
          if (el.checked) {
            selectedBase.add(resource);
          } else {
            selectedBase.delete(resource);
          }
          render();
        });
      });

      wrap.querySelector<HTMLButtonElement>("button[data-store]")?.addEventListener("click", () => {
        storeSelected(base);
      });

      wrap.querySelector<HTMLButtonElement>("button[data-retrieve]")?.addEventListener("click", () => {
        retrieveSelected(base);
      });
    }
  }

  // Moves each selected ship-cargo item into the Base's storage, capped by
  // that item's own category free space (baseStorage.ts) -- a partial
  // move (category filled mid-transfer) leaves the remainder in ship
  // cargo rather than discarding it, same convention as wreck looting.
  function storeSelected(base: BaseRecord): void {
    if (selected.size === 0) return;
    let movedAny = false;
    selected.forEach((resource) => {
      const available = state.cargo[resource] ?? 0;
      if (available <= 0) return;
      const want = pendingQty(resource, available, qtyByItem);
      if (want <= 0) return;
      const moved = transferToBase(base, resource, want);
      if (moved > 0) {
        movedAny = true;
        state.cargo[resource] = available - moved;
        if (state.cargo[resource] <= 0) delete state.cargo[resource];
      }
    });
    selected.clear();
    qtyByItem.clear();
    if (movedAny) {
      state.lastEvent = "Transferred cargo into Base storage.";
      triggerSave(ctx);
    }
    render();
  }

  // Moves each selected Base-storage item back into ship cargo, capped by
  // the ship's real free cargo space (freeCargoM3, shared with Mining/
  // Market/wreck looting) -- a partial move leaves the remainder in Base
  // storage.
  function retrieveSelected(base: BaseRecord): void {
    if (selectedBase.size === 0) return;
    let movedAny = false;
    selectedBase.forEach((resource) => {
      // Whichever hold this item actually draws on (16.39) -- retrieving
      // ore from a Base fills the ore hold, not general cargo.
      const freeVol =
        resource in RESOURCE_TIER
          ? freeOreHoldM3(ship.oreHoldM3, state.cargo, state.acceptedMissions)
          : freeCargoM3(ship.cargoM3, state.cargo, state.acceptedMissions);
      if (freeVol <= 0) return;
      // freeVol is real m3; base.storage/want are UNITS (16.40) -- convert
      // before comparing, or a bulky high-tier ore's free-space cap would
      // be checked against the wrong number entirely.
      const freeUnits = freeVol / volumePerUnitM3(resource);
      const available = base.storage[resource] ?? 0;
      const want = pendingQty(resource, available, qtyByBaseItem);
      if (want <= 0) return;
      const moved = transferFromBase(base, resource, Math.min(want, freeUnits));
      if (moved > 0) {
        movedAny = true;
        state.cargo[resource] = (state.cargo[resource] ?? 0) + moved;
      }
    });
    selectedBase.clear();
    qtyByBaseItem.clear();
    if (movedAny) {
      state.lastEvent = "Retrieved cargo from Base storage.";
      triggerSave(ctx);
    }
    render();
  }

  function sellSelected(): void {
    const hasStation = getSystem(state.currentSystemId)?.hasStation ?? false;
    if (!hasStation || selected.size === 0) return;
    let earned = 0;
    selected.forEach((resource) => {
      const available = state.cargo[resource] ?? 0;
      const sold = pendingQty(resource, available, qtyByItem);
      if (sold <= 0) return;
      earned += sold * marketPricePerUnit(resource);
      const remaining = available - sold;
      if (remaining > 0) {
        state.cargo[resource] = remaining;
      } else {
        delete state.cargo[resource];
      }
    });
    state.credits += Math.round(earned);
    state.lastEvent = `Sold cargo for ${Math.round(earned).toLocaleString()} CR.`;
    selected.clear();
    qtyByItem.clear();
    triggerSave(ctx);
    ctx.rerender();
  }

  // Jettison (Section 11a, in-space/attended context only): dumps cargo
  // into space for nothing, the escape hatch for a hold full of low-value
  // salvage when something better is floating nearby. Deliberately gives
  // no credits and leaves no recoverable container -- the doc frames it as
  // discarding, not as creating a lootable wreck.
  function jettisonSelected(): void {
    if (state.lastTopLevelView !== "expedition" || selected.size === 0) return;
    let dumpedM3 = 0; // real volume freed -- want is in UNITS (16.40), never directly a m3 figure
    selected.forEach((resource) => {
      const available = state.cargo[resource] ?? 0;
      const want = pendingQty(resource, available, qtyByItem);
      if (want <= 0) return;
      dumpedM3 += want * volumePerUnitM3(resource);
      const remaining = available - want;
      if (remaining > 0) {
        state.cargo[resource] = remaining;
      } else {
        delete state.cargo[resource];
      }
    });
    selected.clear();
    qtyByItem.clear();
    if (dumpedM3 > 0) {
      state.lastEvent = `Jettisoned ${dumpedM3.toFixed(1)} m3 of cargo.`;
      triggerSave(ctx);
    }
    render();
  }

  render();
}

function sortLabel(mode: SortMode): string {
  return mode === "value" ? "Value" : mode === "volume" ? "Volume" : "Type";
}

// `qty` is how much of this stack the pending action will actually move,
// in the same UNITS the row itself is stored in (16.40) -- the
// stack-splitting UI Section 15 lists as an open item ("partial
// jettison/sell/transfer of a single cargo item stack"), now real. Defaults
// to the whole stack, so the common case is unchanged: tick and act.
function cargoRowHtml(
  row: CargoRow,
  isSelected: boolean,
  selectAttr: "data-select" | "data-select-base",
  qty: number,
  qtyAttr: "data-qty" | "data-qty-base"
): string {
  const shown = Math.min(qty, row.qty);
  // A real resource's per-tier volume (16.40) means the unit count and the
  // m3 it occupies genuinely differ -- show both. Anything at vpu===1
  // (every Market good, salvage, artifact) would just repeat the same
  // number twice, so that parenthetical is skipped for those.
  const volumeNote = row.vpu !== 1 ? ` <span class="dim">(${row.m3.toFixed(1)} m3)</span>` : "";
  return `
    <div class="cargo-row">
      <label>
        <input type="checkbox" ${selectAttr}="${row.resource}" ${isSelected ? "checked" : ""} />
        <span class="cargo-row-name">${row.resource}</span>
        <span class="pill">${row.group}</span>
      </label>
      <span class="dim">${row.qty.toFixed(1)} units${volumeNote}</span>
      <label class="cargo-qty">
        <span class="dim">qty</span>
        <input type="number" ${qtyAttr}="${row.resource}" min="0.1" max="${row.qty}" step="0.1"
          value="${shown.toFixed(1)}" ${isSelected ? "" : "disabled"} />
      </label>
      <span class="dim">${row.pricePerUnit.toFixed(2)} CR/unit</span>
      <span>${Math.round(row.pricePerUnit * shown).toLocaleString()} CR</span>
      ${bestMarketHtml(row)}
    </div>
  `;
}

/**
 * The best market for a row, as a waypoint button (16.98).
 *
 * Same idiom and same toggle semantics as the Market screen's Highest Demand
 * column -- price above, place below, pressing the one already set clears it --
 * because they answer the same question and a captain should not have to learn
 * two controls for it. Carries the hop count, since a better price six jumps out
 * is not obviously better.
 *
 * Base-storage rows pass no `best`, so this renders nothing there: goods sitting
 * in a warehouse are not being routed anywhere.
 */
function bestMarketHtml(row: CargoRow): string {
  if (!row.best) return "";
  // Already standing in the best market: a waypoint to where you are is not a
  // course, it is a no-op with a star on it. Say so instead.
  if (row.best.systemId === state.currentSystemId) {
    return `<span class="cargo-best-here" title="Nowhere pays more for ${row.resource} right now">
      ${row.best.pricePerUnit.toFixed(2)} · best here</span>`;
  }
  const isSet = state.waypointSystemId === row.best.systemId;
  const hops = row.best.hops === null ? "" : ` · ${row.best.hops} hop${row.best.hops === 1 ? "" : "s"}`;
  return `
    <button type="button" class="cargo-best mk-way ${isSet ? "mk-way-set" : ""}"
            data-cargo-waypoint="${row.best.systemId}"
            title="${
              isSet
                ? `Clear the waypoint on ${row.best.systemName}`
                : `${row.best.systemName} pays the most for ${row.resource} — ${row.best.pricePerUnit.toFixed(2)} CR/unit. Set course.`
            }">
      <span class="mk-way-price">${row.best.pricePerUnit.toFixed(2)}</span>
      <span class="mk-way-where">${row.best.systemName}${hops}${isSet ? " ★" : ""}</span>
    </button>
  `;
}

// Base storage panel (Don's direction, 16.36): one section per named
// storage category (baseStorage.ts) with its own m3 cap, real items
// stored in it, and a checkbox-select for Retrieve -- mirrors the ship
// cargo column's own layout so the two sides read as one consistent UI.
function baseStoragePanelHtml(
  base: BaseRecord,
  selectedBase: Set<string>,
  qtyFor: (name: string, available: number) => number
): string {
  const rows = sortRows(baseRows(base), "type");
  const categories = Object.keys(BASE_STORAGE_CAPS) as (keyof typeof BASE_STORAGE_CAPS)[];
  const selectedCount = rows.filter((r) => selectedBase.has(r.resource)).length;
  return `
    <div class="cargo-column">
      <h2>Base Storage</h2>
      <div class="dim">
        ${categories.map((c) => `${c}: ${categoryUsedM3(base, c).toFixed(0)} / ${categoryFreeM3(base, c) + categoryUsedM3(base, c)} m3`).join(" · ")}
      </div>
      <div class="cargo-list">
        ${
          rows.length === 0
            ? '<p class="dim">Base storage is empty.</p>'
            : rows
                .map((r) =>
                  cargoRowHtml(r, selectedBase.has(r.resource), "data-select-base", qtyFor(r.resource, r.qty), "data-qty-base")
                )
                .join("")
        }
      </div>
      <div class="cargo-actions">
        <span class="dim">${selectedCount > 0 ? `${selectedCount} selected` : "Select stored cargo"}</span>
        <button type="button" data-retrieve ${selectedCount > 0 ? "" : "disabled"}>← Retrieve Selected</button>
      </div>
    </div>
  `;
}
